import React from 'react';
import PanelLogic from './PanelLogic';
import PanelTemplate from './PanelTemplate';
import { dateOf, dateLabel, detailGeometry, readDetail, number, minuteOf } from './realData';
import { seriesEntries, periodLabel as groupedPeriodLabel } from '../panelData';
import { buildPreAlarmContextPresentation } from '../../../../Components/Charts/LineChart/preAlarmContext';
import { fmt, timeLabel, safeValues } from "./utils/presentationSanitizers";
import { applyRecalcToScoreData } from '../../FeederScore/recomputeFeederScore';
import { areaSummaryValues } from './utils/areaSummaryValues';
export default
/**
 * Adapta los datos reales y las integraciones de la aplicación al motor visual.
 *
 * Extiende PanelLogic sin cambiar su contrato y reemplaza únicamente las fuentes
 * sintéticas del prototipo por respuestas y callbacks del frontend productivo.
 */
class PanelRuntime extends PanelLogic {
  static getDerivedStateFromProps(props, state) {
    if (props.model === state.model && props.groupMode === state.externalGroup && props.compact === state.externalCompact && props.selectedRateKey === state.externalRate && props.nivelSeleccionado === state.externalLevel) return null;
    const index = props.model.cages.indexOf(props.jaulaSeleccionada);
    const sel = index >= 0 ? index : Math.min(state.sel || 0, Math.max(0, props.model.rows.length - 1));
    const aggregate = props.nivelSeleccionado !== 'centro';
    const sameEntities = props.nivelSeleccionado === state.externalLevel
      && props.model.rows.length === state.model?.rows.length
      && props.model.rows.every((row, ci) => row.id === state.model.rows[ci].id);
    const selectedIds = new Set([state.sel, ...(state.cmp8 || [])].map(ci => state.model?.rows[ci]?.id));
    return {
      model: props.model,
      externalLevel: props.nivelSeleccionado,
      externalGroup: props.groupMode,
      externalCompact: props.compact,
      externalRate: props.selectedRateKey,
      compact: props.compact,
      tRes: {
        day: 'dia',
        week: 'sem',
        month: 'mes'
      }[props.groupMode],
      sel,
      day: Math.min(state.day || 0, Math.max(0, props.model.days.length - 1)),
      hover: null,
      hi: null,
      ah: null,
      nh: null,
      detailHoverTime: null,
      rkHover: null,
      rkHd: null,
      sZoom: null,
      zm: null,
      drag: null,
      // Area responses contain daily center summaries. Start with the whole
      // area, and retain the user's center selection when only dates change.
      cmp8: aggregate ? props.model.rows.flatMap((row, ci) => ci !== sel && (!sameEntities || selectedIds.has(row.id)) ? [ci] : []) : null,
      areaSummaryPeriod: null,
      ...(aggregate && !sameEntities ? { view: ['cmp', 'dep', 'cz'].includes(state.view) ? 'panel' : state.view, exp: null } : {}),
      vsSel: null,
      vsPend: null,
      fsHov: null,
      fsSel: null,
      dpHov: null,
      cFocus: null,
      cDays: null,
      cEnd: null
    };
  }
  get full() {
    if (this._sourceModel !== this.props.model || this._sourceScore !== this.props.scores || this._sourceScoreRecalc !== this.props.scoreRecalcParams) {
      this._sourceModel = this.props.model;
      this._sourceScore = this.props.scores;
      this._sourceScoreRecalc = this.props.scoreRecalcParams;
      this._dm = {};
      this._dk = null;
      this._czp = null;
      this._scd = null;
      this._da = null;
      this._rk = null;
    }
    return this.props.model;
  }
  get data() {
    return this.full;
  }
  setState(update, callback) {
    super.setState((previous, props) => {
      const next = typeof update === 'function' ? update(previous, props) : update;
      if (!next) return next;
      const changed = (next.sel != null && next.sel !== previous.sel)
        || (next.day != null && next.day !== previous.day);
      return changed ? {
        ...next,
        detailHoverTime: null,
        ah: null,
        hi: null
      } : next;
    }, () => {
      const mode = {
        dia: 'day',
        sem: 'week',
        mes: 'month'
      }[this.state.tRes] || 'day';
      if (mode !== this.props.groupMode) this.props.setGroupMode(mode);
      if (!!this.state.compact !== !!this.props.compact) this.props.onCompactClick();
      const name = this.data.cages[this.state.sel] || '';
      if (name !== this.props.jaulaSeleccionada) this.props.setJaulaSeleccionada(name);
      if (this.state.view === 'fs') this.props.loadScores();
      callback?.();
    });
  }
  componentWillUnmount() {
    super.componentWillUnmount();
    clearTimeout(this._rkT);
    clearTimeout(this._dsRT);
  }
  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);
    if (this.state.view === 'dep' && !this.hasDepthData()) {
      this.setState({
        view: 'panel',
        dpHov: null
      });
    }
  }
  calDate(index) {
    const day = dateOf(this.data.days[0]);
    day.setDate(day.getDate() + index);
    return day;
  }
  calVals(state) {
    const values = super.calVals(state);
    return {
      ...values,
      calApply: () => {
        if (this.state.calB == null) return;
        const a = Math.min(this.state.calA ?? 0, this.state.calB),
          b = Math.max(this.state.calA ?? 0, this.state.calB);
        const payload = {
          startDate: dateLabel(this.calDate(a)).replaceAll('/', '-'),
          endDate: dateLabel(this.calDate(b)).replaceAll('/', '-'),
          dayRange: b - a + 1
        };
        this.props.setDias(payload);
        try {
          localStorage.setItem('RangoDiasScapp', JSON.stringify(payload));
        } catch (_error) {}
        this.setState({
          calOpen: false,
          calA: null,
          calB: null,
          calHv: null,
          calVM: null,
          rngA: null,
          rngB: null,
          day: 0
        });
      }
    };
  }
  periodBuckets(weekly) {
    const mode = this.props.groupMode === 'day' && weekly ? 'week' : this.props.groupMode;
    const result = new Map();
    this.data.days.forEach((day, index) => {
      const date = dateOf(day);
      if (mode === 'week') date.setDate(date.getDate() - (date.getDay() + 6) % 7);
      if (mode === 'month') date.setDate(1);
      const key = dateLabel(date);
      if (!result.has(key)) result.set(key, []);
      result.get(key).push(index);
    });
    return [...result.values()];
  }
  periodLabel(indices, withRange = false) {
    if (!indices?.length) return '';
    const first = this.data.days[indices[0]];
    const last = this.data.days[indices[indices.length - 1]];
    const mode = this.props.groupMode || 'day';
    const label = groupedPeriodLabel(first, mode);
    return withRange && mode !== 'day' ? label + ' · ' + first + ' – ' + last : label;
  }
  /** Obtiene y memoriza la geometría del detalle para una jaula y un día. */
  detail(ci, di) {
    const rateKey = this.props.selectedRateKey || 'tasa';
    const data = this.data;
    const key = `${ci}:${di}:${rateKey}:${!!this.props.showReaction}`;
    this._dm ||= {};
    return this._dm[key] ||= detailGeometry(readDetail(data.rows[ci], data.days[di], this.props.showReaction, rateKey));
  }
  hasDepthData() {
    return this.data.rows.some((row, ci) => this.data.days.some((_, di) => this.detail(ci, di).pts.some(point => Number.isFinite(point.dep))));
  }
  activeAlarm(det) {
    if (this.state.ah != null && det.alarms[this.state.ah]) return det.alarms[this.state.ah];
    const minute = this.state.detailHoverTime;
    if (!Number.isFinite(minute)) return null;
    const span = (this.state.zm?.[1] ?? det.timeMax) - (this.state.zm?.[0] ?? det.timeMin);
    const tolerance = Math.min(2.5, span * .01);
    return det.alarms.find(alarm => Math.abs(alarm.sec / 60 - minute) <= tolerance) || null;
  }
  detailValue(det, key, minute, alarm = null) {
    const snapshotKey = /^act\d+$/.test(key) ? 'activity' : {
      act: 'activity',
      oxy: det.oxygenKey || 'so2',
      temp: 'temp',
      cur: 'cs',
      dir: 'cd',
      dep: 'depth',
      tasa: det.selectedRateKey || 'tasa',
      ppaC: 'perdidos'
    }[key];
    const exact = number(alarm?.snapshot?.[snapshotKey]);
    if (Number.isFinite(exact)) return exact;
    const samples = det.series?.[key] || [];
    const closest = samples.reduce((best, sample) => !best || Math.abs(sample.t - minute) < Math.abs(best.t - minute) ? sample : best, null);
    return closest?.value ?? NaN;
  }
  detHover(det, thr) {
    const out = super.detHover(det, thr);
    const [start, end] = this.state.zm || [det.timeMin, det.timeMax];
    out.hMove = event => {
      const pan = this._dpan;
      if (pan && end - start < det.timeMax - det.timeMin) {
        const dx = event.clientX - pan.x;
        if (Math.abs(dx) > 2) {
          pan.moved = true;
          const nextStart = pan.z0 - dx / pan.w * (end - start);
          this.setZoom(nextStart, nextStart + end - start, det.timeMin, det.timeMax);
          return;
        }
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      this.setState({
        detailHoverTime: start + fraction * (end - start),
        hi: null
      });
    };
    const leave = out.hLeave;
    out.hLeave = () => {
      leave();
      this.setState({
        detailHoverTime: null
      });
    };
    const alarm = this.activeAlarm(det);
    const minute = alarm ? alarm.sec / 60 : this.state.detailHoverTime;
    const fields = {
      vA: 'act',
      vT: 'tasa',
      vP: 'ppaC',
      vO: 'oxy',
      vTe: 'temp',
      vC: 'cur',
      vD: 'dep'
    };
    for (const [field, key] of Object.entries(fields)) {
      const values = (det.series?.[key] || []).map(p => p.value);
      out[field] = Number.isFinite(minute) ? fmt(this.detailValue(det, key, minute, alarm)) : values.length ? `${fmt(Math.min(...values))}–${fmt(Math.max(...values))}` : 'Sin datos';
    }
    const activityKeys = (det.activities || []).map(activity => activity.key);
    if (activityKeys.length) {
      out.vA = activityKeys.map(key => {
        const values = (det.series?.[key] || []).map(point => point.value);
        return Number.isFinite(minute) ? fmt(this.detailValue(det, key, minute, alarm)) : values.length ? `${fmt(Math.min(...values))}–${fmt(Math.max(...values))}` : 'Sin datos';
      }).join(' / ');
    }
    if (Number.isFinite(minute)) {
      const direction = this.detailValue(det, 'dir', minute, alarm);
      Object.assign(out, {
        hTime: timeLabel(minute),
        hx: `${(minute - start) / (end - start) * 100}%`,
        hDisp: 'block',
        vOp: '1',
        vCd: Number.isFinite(direction) ? `${det.card(direction)} · ${fmt(direction)}°` : '—',
        vCdRot: Number.isFinite(direction) ? String(direction) : '0'
      });
    }
    out.zArrows = (det.directionSamples || []).filter(p => p.t >= start && p.t <= end).map(p => ({
      left: `${(p.t - start) / (end - start) * 100}%`,
      rot: String(p.value),
      op: '0.8',
      size: '12'
    }));
    return out;
  }

  // Without reaction access (or data) the attended state is unknown; like v1, show
  // plain alarms instead of painting every one as "No atendida".
  alarmVals(det) {
    const out = super.alarmVals(det);
    const alarm = this.activeAlarm(det);
    if (alarm) {
      const c = '#e4e9f0';
      const row = (l, v) => ({
        l,
        v,
        c
      });
      const context = buildPreAlarmContextPresentation(alarm.preContext);
      out.alL = {
        ...out.alL,
        badge: alarm.att == null ? 'Alarma' : alarm.att ? 'Atendida' : 'No atendida',
        rows: [row('Inicio', timeLabel(alarm.sec / 60)), row('Fin', timeLabel((alarm.sec + alarm.dur) / 60)), row('Duración', `${fmt(alarm.dur)} s`), row('Det. máximas', fmt(alarm.dmax)), row('Det. totales', fmt(alarm.dtot)), ...(this.props.showReaction ? [row('Reacción', Number.isFinite(alarm.react) ? `${fmt(alarm.react)} s` : '—')] : []), ...(context ? context.insufficient ? [row('Contexto previo', 'Datos insuficientes')] : [row('Antes de la alarma', `${fmt(context.spanSec)} s${context.partial ? ' (parcial)' : ''}`), row('Sin detecciones', `${fmt(context.noDetectionPct)} %`), row('Detección típica', fmt(context.positiveMedian)), row('Detección máxima previa', fmt(context.positiveMax)), row('Ya había alarma antes', context.hadPreviousAlarm ? 'Sí' : 'No')] : [])]
      };
    }
    if (!det.alarms.length || det.alarms.some(a => a.att != null)) return out;
    const NEU = 'oklch(0.76 0.11 70)';
    const plain = p => ({
      ...p,
      c: NEU,
      fill: NEU
    });
    out.alarmPins = (out.alarmPins || []).map(p => ({
      ...plain(p),
      h: "calc(100% - 1.481481vh)"
    }));
    out.alList = (out.alList || []).map(p => ({
      ...plain(p),
      react: '—'
    }));
    if (out.alL) {
      out.alL = out.alL.badge === 'No atendida' ? {
        ...out.alL,
        c: NEU,
        fill: 'transparent',
        badge: 'Alarma',
        rows: out.alL.rows.filter(r => r.l !== 'Reacción')
      } : {
        ...out.alL,
        rows: out.alL.rows.filter(r => !['No atendidas', 'Atendidas', 'Reacción prom.'].includes(r.l))
      };
    }
    if (out.alTipOn) out.alTip = {
      ...out.alTip,
      c: NEU,
      estado: 'Alarma',
      react: '—'
    };
    out.alNo = '—';
    out.alYes = '—';
    out.alAvg = '—';
    return out;
  }
  feedingHours(ci) {
    const row = this.data.rows[ci];
    const selectedDays = new Set(this.data.days);
    const readRange = (value, day) => {
      const range = value?.feeding_time_range || value;
      const start = minuteOf(range?.inicio?.hora ?? range?.inicio);
      const end = minuteOf(range?.fin?.hora ?? range?.fin ?? range?.final?.hora ?? range?.final);
      return Number.isFinite(start) && Number.isFinite(end) ? {
        start: start / 60,
        end: end / 60,
        day
      } : null;
    };
    const declared = [...seriesEntries(row?.node?.feeding_time_range).map(entry => ({
      ...entry,
      normalizedDay: dateLabel(dateOf(entry.day))
    })).filter(entry => selectedDays.has(entry.normalizedDay)).map(entry => readRange(entry.value, entry.normalizedDay)), ...(row?.cells || []).map((cell, di) => readRange(cell?.summary, this.data.days[di]))].filter(Boolean);
    if (declared.length) {
      const first = declared.reduce((best, range) => range.start < best.start ? range : best);
      const last = declared.reduce((best, range) => range.end > best.end ? range : best);
      return [first.start, last.end, first.day, last.day];
    }

    // Older responses may omit feeding_time_range; retain graph data as fallback.
    const measured = this.data.days.flatMap((day, di) => {
      const times = this.detail(ci, di).pts.filter(p => p.tasa > 0).map(p => p.t / 60);
      return times.length ? [{
        start: Math.min(...times),
        end: Math.max(...times),
        day
      }] : [];
    });
    if (!measured.length) return [NaN, NaN, null, null];
    const first = measured.reduce((best, range) => range.start < best.start ? range : best);
    const last = measured.reduce((best, range) => range.end > best.end ? range : best);
    return [first.start, last.end, first.day, last.day];
  }
  czPts() {
    if (this._czp) return this._czp;
    return this._czp = this.data.rows.flatMap((row, ci) => this.data.days.flatMap((_, d) => this.detail(ci, d).pts.flatMap((p, i) => p.tasa > 0 && Number.isFinite(p.ppa) ? [{
      ...p,
      ci,
      d,
      i
    }] : [])));
  }
  depthBands(det, width, low, high) {
    if (!Number.isFinite(low) || !Number.isFinite(high)) return [];
    return super.depthBands({
      ...det,
      pts: det.pts.map(p => ({
        ...p,
        ppa: Number.isFinite(p.ppa) ? p.ppa : 0,
        tasa: Number.isFinite(p.tasa) ? p.tasa : 0
      }))
    }, width, low, high);
  }
  depthProfile(det, height) {
    const pts = det.pts.filter(p => Number.isFinite(p.dep));
    if (!pts.length) return {
      dHeat: [],
      dIns: {
        lab: 'Sin profundidad',
        c: '#8793a6',
        txt: '· no hay mediciones de winche'
      },
      dMetOpts: []
    };
    // Alarm indices refer to the full slot array, not a filtered copy.
    return super.depthProfile(det, height);
  }
  depAgg(ci, di) {
    const det = this.detail(ci, di),
      pts = det.pts,
      measured = pts.filter(p => Number.isFinite(p.dep));
    let rep = 0,
      transit = 0;
    for (let i = 1; i < pts.length; i++) {
      if (!Number.isFinite(pts[i].dep) || !Number.isFinite(pts[i - 1].dep)) continue;
      if (Math.abs(pts[i].dep - pts[i - 1].dep) > .35) transit += 5;
      if (Math.floor(pts[i].dep) !== Math.floor(pts[i - 1].dep)) rep++;
    }
    return {
      B: this.depthBands(det, 1, 0, 24),
      fast: !!this.data.rows[ci].cells[di].ay,
      rep,
      op: NaN,
      tr: transit,
      nd: (pts.length - measured.length) * 5,
      alN: det.alarms.filter(alarm => !Number.isFinite(pts[alarm.i]?.dep)).length,
      alLow: det.alarms.filter(alarm => Number.isFinite(pts[alarm.i]?.dep) && pts[alarm.i].dep < .5).length,
      dMin: measured.length ? Math.min(...measured.map(p => p.dep)) : NaN,
      dMax: measured.length ? Math.max(...measured.map(p => p.dep)) : NaN
    };
  }
  /** Normaliza los registros de Feeder Score y los cruza con las entidades visibles. */
  scoreData() {
    const data = this.data;
    if (this._scd) return this._scd;
    const recalcParams = this.props.scoreRecalcParams;
    const exactRegion = this.props.nivelSeleccionado === 'region' && recalcParams?.mode === 'standard' && Boolean(recalcParams?.exact);
    const scoreSource = applyRecalcToScoreData(this.props.scores, recalcParams, { groupRegionCenters: exactRegion });
    const M = data.rows.map(row => data.days.map((day, di) => {
      const scoreDay = Object.keys(scoreSource || {}).find(candidate => dateLabel(dateOf(candidate)) === day);
      const records = scoreSource?.[day] || scoreSource?.[scoreDay] || {};
      const normalizeId = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const candidateIds = [row.id, row.id?.split(':').pop(), row.name, ...(row.node?.note_camera_ids || [])].map(normalizeId).filter(Boolean);
      const record = records[row.id] || records[row.id?.split(':').pop()] || Object.entries(records).find(([recordId, doc]) => candidateIds.includes(normalizeId(recordId)) || candidateIds.includes(normalizeId(doc?.name)))?.[1];
      if (!record) return null;
      const rawScore = number(record.score);
      const rawAlarmTotal = number(record.alarmas_total ?? record.reasons_summary?.alarmas_total);
      const rawAlarmUnattended = number(record.alarmas_no_atendidas ?? record.reasons_summary?.alarmas_no_atendidas);
      const rawFood = number(record.delivered_food_kg);
      const rawReaction = number(record.reaction_time?.mean_sec);
      const rawReactionCount = number(record.reaction_time?.n);
      const reactionSamples = (record.reaction_time?.samples_sec || []).map(Number).filter(Number.isFinite);
      if (!reactionSamples.length && Number.isFinite(rawReaction) && rawReactionCount > 0) {
        reactionSamples.push(...Array.from({ length: rawReactionCount }, () => rawReaction));
      }
      const al = Number.isFinite(rawAlarmTotal) ? rawAlarmTotal : 0;
      const un = Number.isFinite(rawAlarmUnattended) ? rawAlarmUnattended : 0;
      const cell = row.cells[di];
      return {
        // Preserve the server audit (cycles, dimensions, reasons and events) so
        // Feeder Score V2 can open the same detailed breakdown as V1.
        ...record,
        name: record.name || row.name,
        score: Number.isFinite(rawScore) ? rawScore : null,
        att: Math.max(0, al - un),
        al,
        kg: Number.isFinite(rawFood) ? rawFood : 0,
        react: Number.isFinite(rawReaction) ? rawReaction : null,
        rs: reactionSamples,
        rw: record.rw || null,
        sfr: cell.sfr,
        ppa: cell.ppa,
        comp: {} // The prototype's synthetic dimensions do not represent the server's scoring model.
      };
    }));
    const values = M.flat().filter(item => item && Number.isFinite(item.score));
    return this._scd = {
      M,
      W: {},
      flat: values.length ? values.reduce((sum, item) => sum + item.score, 0) / values.length : NaN
    };
  }
  drawerVals(state) {
    const values = super.drawerVals(state);
    values.insightAllowed = !!this.props.insightAllowed;
    values.openInsight = () => {
      this.setState({
        drawer: false,
        drMode: null
      });
      this.props.setView?.('insight');
    };
    values.dashboardVersion = 'v2';
    values.changeDashboardVersion = version => {
      this.setState({
        drawer: false,
        drMode: null
      });
      this.props.setDashboardVersion(version);
      this.props.setView?.('dashboard');
    };
    values.drawerSecs = values.drawerSecs.filter(section => this.props.feederAllowed || section.title !== 'Métricas de alimentación');
    values.drawerSecs.forEach(section => section.groups.forEach(group => {
      group.items = group.items.filter(item => (this.props.nivelSeleccionado === 'centro' || !['Compactar', 'Profundidad', 'Causas'].includes(item.label)) && (this.state.showDailyCauseAnalysis || item.label !== 'Causas'));
    }));
    return values;
  }
  /** Completa el modelo de presentación y delega el marcado en PanelTemplate. */
  render() {
    const values = this.renderVals();
    values.aggregateView = this.props.nivelSeleccionado !== 'centro';
    values.tierScope = { centro: 'centro', area: 'área', region: 'región' }[this.props.nivelSeleccionado] || 'centro';
    if (values.aggregateView && !this.data.empty) {
      values.areaSummary = areaSummaryValues(this);
      values.rankingColumns = '1.041667vw minmax(4.5vw,1.4fr) repeat(4,minmax(0,1fr))';
      Object.assign(values, {
        gridRows: this.state.exp === 'evo' ? 'minmax(0,1fr)' : 'minmax(0,0.85fr) minmax(0,1.15fr)',
        overviewRow: '1 / 3',
        bottomCol: this.state.exp === 'evo' ? '1' : '2',
        evoH: 'auto',
        sfrW: this.state.exp === 'evo' ? 'auto' : '50%',
      });
      // Missing aggregate SFR is not necessarily a fasting center.
      const bucket = this.periodBuckets().find(indices => indices.includes(this.state.hover?.di ?? this.state.day)) || [];
      const fasting = ci => bucket.some(di => this.data.rows[ci].cells[di]?.ay);
      if (values.fBig === 'Ayuno' && !fasting(this.state.sel)) values.fBig = 'Sin datos';
      values.fVals?.forEach((item, i) => {
        const ci = [this.state.sel, ...(this.state.cmp8 || []).filter(ci => ci !== this.state.sel)][i];
        if (item.val === 'ayuno' && !fasting(ci)) item.val = 'Sin datos';
      });
    }
    const metricVisibility = values.det?.visibility || {};
    Object.assign(values, {
      showPrimary: !!metricVisibility.primary,
      showEnvironment: !!metricVisibility.environment,
      showCurrent: !!metricVisibility.currentBlock,
      showDepth: !!metricVisibility.depth,
      hasActivity: !!metricVisibility.activity,
      hasRate: !!metricVisibility.rate,
      hasPpa: !!metricVisibility.ppa,
      hasOxygen: !!metricVisibility.oxygen,
      hasTemperature: !!metricVisibility.temperature,
      hasCurrent: !!metricVisibility.current,
      hasDirection: !!metricVisibility.direction
    });
    const hiddenDetailMetrics = new Set(this.state.hiddenDetailMetrics || []);
    const detailMetricVisible = key => !hiddenDetailMetrics.has(key);
    Object.assign(values, {
      showActivitySeries: values.hasActivity && detailMetricVisible('activity'),
      showRateSeries: values.hasRate && detailMetricVisible('rate'),
      showPpaSeries: values.hasPpa && detailMetricVisible('ppa'),
      showOxygenSeries: values.hasOxygen && detailMetricVisible('oxygen'),
      showTemperatureSeries: values.hasTemperature && detailMetricVisible('temperature'),
      showCurrentSeries: values.hasCurrent && detailMetricVisible('current'),
      showDirectionSeries: values.hasDirection && detailMetricVisible('direction'),
      showDepthSeries: values.showDepth && detailMetricVisible('depth'),
      toggleDetailMetric: key => this.setState(previous => {
        const hidden = new Set(previous.hiddenDetailMetrics || []);
        hidden.has(key) ? hidden.delete(key) : hidden.add(key);
        return { hiddenDetailMetrics: [...hidden] };
      })
    });
    if (values.dH) {
      const original = values.dH;
      const total = original.a + original.o + original.c + original.d;
      const visibleWeight = (values.showPrimary ? original.a : 0) + (values.showEnvironment ? original.o : 0) + (values.hasCurrent ? original.c : 0) + (values.showDepth ? original.d : 0);
      const scaled = (visible, weight) => visible && visibleWeight ? Math.round(total * weight / visibleWeight) : 0;
      values.dH = {
        ...original,
        a: scaled(values.showPrimary, original.a),
        o: scaled(values.showEnvironment, original.o),
        c: scaled(values.hasCurrent, original.c),
        d: scaled(values.showDepth, original.d)
      };
    }
    if (values.det?.series?.tasa?.some(point => point.value > 0)) values.detSummary = values.det.summary;
    values.locationSlot = this.props.locationSlot;
    values.itgSlot = this.props.itgSlot;
    values.languageSlot = this.props.languageSlot;
    values.measurementSlot = this.props.measurementSlot;
    values.downloadData = this.props.downloadData;
    values.canExplainDay = this.state.showDailyCauseAnalysis && this.czPts().some(point => point.ci === this.state.sel && point.d === this.state.day);
    values.text = value => typeof value === 'string' && this.props.nivelSeleccionado !== 'centro' ? value.replace(/Jaulas/g, 'Centros').replace(/jaulas/g, 'centros').replace(/Jaula/g, 'Centro').replace(/jaula/g, 'centro') : value;
    if (values.navItems) values.navItems = values.navItems.filter(item => this.props.feederAllowed || item.label !== 'Alimentación');
    const noScores = this.state.view === 'fs' && !this.scoreData().M.flat().some(Boolean);
    const noCauses = this.state.view === 'cz' && values.czEmpty;
    const noDepth = this.state.view === 'dep' && !this.hasDepthData();
    if (noScores || noCauses || noDepth) {
      values.viewFs = false;
      values.viewCz = false;
      values.viewDep = false;
      values.noData = true;
      values.noDataT = noScores ? this.props.scoreLoading ? 'Cargando alimentación…' : 'Sin métricas de alimentación' : noDepth ? 'Sin mediciones de profundidad' : 'Sin muestras suficientes para analizar causas';
      values.noDataS = noScores ? this.props.scoreError || 'No hay registros del servidor para esta selección.' : 'Prueba otro rango o selecciona un centro con datos horarios.';
    }
    return <PanelTemplate {...safeValues(values)} />;
  }
}
